import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { DeckGL, InfoWidget } from '@deck.gl/react'
import { OrthographicView, PickingInfo, LinearInterpolator } from '@deck.gl/core'
import { TileLayer } from '@deck.gl/geo-layers'
import { BitmapLayer, PolygonLayer, ScatterplotLayer, PathLayer } from '@deck.gl/layers'
import {
    Box, Button, CircularProgress, Drawer, FormControl,
    IconButton,InputAdornment, InputLabel, LinearProgress,
    OutlinedInput, Skeleton, Stack, TextField, Typography
} from '@mui/material'
import '@deck.gl/widgets/stylesheet.css'
import { Accordion } from 'react-bootstrap'
import { MdLocationSearching, MdOutlineMyLocation } from 'react-icons/md'
import { GrFormView, GrFormViewHide } from 'react-icons/gr'
import { FaChevronLeft } from 'react-icons/fa'
import { ChevronRight, Close, OpenInNew, Refresh } from '@mui/icons-material'
import { DataFilterExtension } from '@deck.gl/extensions'

import { Annotation, ColorLibObj, ImageData, MapPropV2, Marker, TypeVersion, Version } from '../../utils/types'
import { getMapProps } from './helper/mapProps'
import { get, post } from '../../utils/http'
import { hexToRgb, sortListAlphaNumeric } from '../../utils/helperFunc'
import { LoadingBtn, MapWarningTexts } from './helper/uiElements'
import { getModifiedGSData, getSysAnnoData } from './helper/processAnnotations'
import { isPrivate, tileAppUrl } from '../../utils/config'
import { Dotpulse, LoadingAnimeSpinner } from '../common/Loaders'
import Versions from './helper/Versions'
import { ViewerContext } from "./helper/contextV2"
import LazyList from '../common/LazyList'
import SysAnnotationsList from './helper/SysAnnotationsListV2'
import UserAnnotationsList from './helper/UserAnnotationsListV2'

interface ImageViewerProps {
    imageData: ImageData
    resetMap: Function
    readonly?: boolean
}

const iconButtonSx = { 
    cursor: "pointer", 
    padding: 0, 
    "&:hover": { color: "#0d6efd" } 
}

interface Polygon {
    id: number | string
    name: string
    baseColor: number[]
    area: string
    coordinates: any[]
}

interface Line {
    id: number | string
    distance: string
    coordinates: any[]
}

interface Point {
    id: number | string
    coordinates: any[]
}

interface LayerDataProp { 
    polygons: Polygon[]
    lines: Line[]
    points: Point[]
    category: string[]
}

const ImageViewer: React.FC<ImageViewerProps> = ({ imageData, resetMap, readonly }) => {
    const [isMapLoading, setIsMapLoading] = useState<boolean>(false)
    const [isDataLoading, setIsDataLoading] = useState<boolean>(false)
    const [mapError, setMapError] = useState<boolean>(false)

    const [mapProp, setMapProp] = useState<MapPropV2 | null>(null)
    const [initialViewState, setInitialViewState] = useState<any>({target: [0, 0, 0], zoom: 0, minZoom: 0})
    const [layerData, setLayerData] = useState<LayerDataProp>({ polygons: [], lines: [], points: [], category: [] })
    
    const [sysGsAnnotations, setSysGsAnnotations] = useState<Marker[]>([])
    const [modifiedGsAnnotations, setModifiedGsAnnotations] = useState<Annotation[]>([])
    const [BCRScore, setBCRScore] = useState<number | null>(null)
    const [colorLib, setColorLib] = useState<ColorLibObj[]>([])

    const [sysMarkerVer, setSysMarkerVer] = useState<number>(0)
    const [versions, setVersions] = useState<TypeVersion[]>([])
    const [selectedVersion, setSelectedVersion] = useState<number>(0)
    const [activeVerId, setActiveVerId] = useState<number>(0)

    const [drawerOpen, setDrawerOpen] = useState<boolean>(false)
    const [activeMarkerLayer, setActiveMarkerLayer] = useState<string>('system')
    const [sysMarkerLoader, setSysMarkerLoader] = useState<boolean>(false)
    const [sysMarkerFilter, setSysMarkerFilter] = useState<string[]>([])
    const [highlightMarkerId, setHighlightMarkerId] = useState<string>('')

    const [userMarkerLoader, setUserMarkerLoader] = useState<boolean>(false)
    const [userMarkerFilter, setUserMarkerFilter] = useState<number[]>([])

    const [showMapCoords, setShowMapCoords] = useState<boolean>(false)
    const [locatePoint, setLocatePoint] = useState<boolean>(false)
    const [pointLoc, setPointLoc] = useState<{x: number, y: number}>({x: 0, y: 0})

    const defaultMarkerColor = [0, 150, 255]

    const colorDict = useMemo(() => {
        const dict = new Map<string, number[]>()
        colorLib.forEach(c => {
            if (c.name && c.color) dict.set(c.name, hexToRgb(c.color))
        })
        return dict
    }, [colorLib])

    // Memoize the view to prevent React from killing and remounting the camera state on re-render
    const views = useMemo(() => new OrthographicView({ id: 'ortho', flipY: true }), [])

    const layers = useMemo(() => {
        if (!mapProp) return []

        const { url, maxZoom, tileSize, customTileData } = mapProp

        return [
            new TileLayer<ImageBitmap | null>({
                id: 'image-tiles',
                tileSize: tileSize,
                minZoom: -maxZoom,
                maxZoom: 0,
                refinementStrategy: 'best-available',
                maxRequests: 16,
                maxCacheSize: 250,
                coordinateSystem: 'cartesian',

                getTileData: async ({ index, signal }) => {
                    const { x, y, z } = index

                    const serverZoom = z + maxZoom
                    
                    if (serverZoom < 0 || serverZoom > maxZoom || x < 0 || y < 0) return null

                    if (customTileData) {
                        try {
                            customTileData.payload.x = x
                            customTileData.payload.y = y
                            customTileData.payload.z = serverZoom

                            const response = await fetch(`${tileAppUrl}/tile/get-tile`, {
                                method: 'POST',
                                ...customTileData.config,
                                signal: signal,
                                body: JSON.stringify(customTileData.payload)
                            })
                            if (!response.ok) return null
                            const blob = await response.blob()
                            return await createImageBitmap(blob)
                        } catch (err) {
                            throw err
                        }
                    } else {
                        try {
                            const tileUrl = url.replace('{x}', String(x)).replace('{y}', String(y)).replace('{z}', String(serverZoom))
                            const response = await fetch(tileUrl, { signal: signal })
                            if (!response.ok) return null
                            const blob = await response.blob()
                            return await createImageBitmap(blob)
                        } catch (err) {
                            throw err
                        }
                    }
                },

                renderSubLayers: (props: any) => {
                    if (!props.data) return null

                    const { boundingBox } = props.tile

                    return new BitmapLayer(props, {
                        ...props,
                        id: `${props.id}-bitmap`,
                        image: props.data,
                        data: [0],
                        coordinateSystem: 'cartesian',
                        bounds: [
                            boundingBox[0][0],
                            boundingBox[1][1],
                            boundingBox[1][0],
                            boundingBox[0][1]
                        ]
                    })
                }
            }),

            // POLYGONS
            new PolygonLayer<any>({
                id: 'polygons',
                data: layerData.polygons,
                getPolygon: (d: any) => d.coordinates,
                getFillColor: (d: any) => [d.baseColor[0], d.baseColor[1], d.baseColor[2], 80],
                getLineColor: (d: any) => {
                    const isHighlighted = d.id === highlightMarkerId
                    return isHighlighted
                        ? [255, 0, 0, 255]
                        : [d.baseColor[0], d.baseColor[1], d.baseColor[2], 255]
                },
                getLineWidth: 2,
                lineWidthUnits: 'pixels',
                pickable: true,
                coordinateSystem: 'cartesian',
                extensions: [new DataFilterExtension({filterSize: 1})],
                getFilterValue: (d: any) => sysMarkerFilter.includes(d.name.toLowerCase()) ? 1 : 0,
                filterRange: [1, 1],
                updateTriggers: {
                    getFillColor: [colorDict],
                    getLineColor: [colorDict, highlightMarkerId],
                    getFilterValue: [sysMarkerFilter.join(',')]
                }
            } as any),

            // LINES
            new PathLayer<any>({ 
                id: 'lines',
                data: layerData.lines,
                getPath: (d: any) => d.coordinates, // Array of [X, Y] points
                getColor: (d: any) => [0, 0, 0, 255], // Solid color
                getWidth: 2,
                widthUnits: 'pixels',
                pickable: true,
                coordinateSystem: 'cartesian'
            }),

            // POINTS (CIRCLES)
            new ScatterplotLayer<any>({
                id: 'scatter-points',
                data: layerData.points,
                getPosition: (d: any) => d.coordinates, // Single [X, Y] array
                getFillColor: (d: any) => [0, 0, 255, 100],
                getLineColor: (d: any) => [0, 0, 255, 255],
                stroked: true,
                getLineWidth: 2,
                lineWidthUnits: 'pixels',
                getRadius: 7,
                radiusUnits: 'pixels',
                pickable: true,
                coordinateSystem: 'cartesian'
            })
        ]
    }, [mapProp, layerData, sysMarkerFilter, highlightMarkerId])

    const initMap = async () => {
        setIsMapLoading(true)
        try {
            const { url, maxZoom, slide_width, slide_height, customTileData } = await getMapProps(imageData)
            
            setMapProp({
                url: url,
                maxZoom: maxZoom,
                width: slide_width,
                height: slide_height,
                tileSize: 256,
                customTileData: customTileData
            })
            setInitialViewState({
                target: [slide_width / 2, slide_height / 2, 0],
                zoom: Math.min(-maxZoom + 2, 0),
                minZoom: -maxZoom
            })
        } catch (err) {
            console.log(err)
            setMapError(true)
        } finally {
            setIsMapLoading(false)
        }
    }

    useEffect(() => {
        initMap()
    }, [])

    const updateAnnoClassLibrary = async () => {
        const res = await get("Annotationlibs/")
        if (res.length) setColorLib(sortListAlphaNumeric(res))
    }

    const getVersion = async () => {
        // Slides in master-dataset directory (internal viewer) will show version from all users
        if (imageData.input_s3 && imageData.input_s3 === 'master-dataset') {
            const res = await get(`Annotations/versions-by-type/${imageData.id}`)
            if (res.length) {
                setVersions(res)
                setSelectedVersion(res[0].id)
            }
        } else {
            // Slides in user directory (My Folder/Mounts/Shared) will only show
            // versions from their own and the users the slide shared with
            const userIds: number[] = [...new Set([...imageData.uploaded_by, ...imageData.shared])]
            const res = await post(`Annotations/versions-by-type/${imageData.id}`, { user_ids: userIds })
            if (res.length) {
                setVersions(res)
                setSelectedVersion(res[0].id)
            }
        }
    }

    const loadRelatedData = async () => {
        setIsDataLoading(true)
        try {
            await updateAnnoClassLibrary()
            await getVersion()
        } catch (err) {
            console.log(err)
        } finally {
            setIsDataLoading(false)
        }
    }

    useEffect(() => {
        if (mapProp) loadRelatedData()
    }, [mapProp])

    const loadAnnotations = async (signal: AbortSignal) => {
        setIsDataLoading(true)
        try {
            setActiveVerId(selectedVersion)

            const res1 = await getModifiedGSData(selectedVersion)
            const res2 = await getSysAnnoData(res1.sys_marker_ver, signal)

            setSysMarkerVer(res1.sys_marker_ver)
            setModifiedGsAnnotations(res1.custom_markers)
            setSysGsAnnotations(res2.markers)
        } catch (err) {
            console.log(err)
        } finally {
            setIsDataLoading(false)
        }
    }

    useEffect(() => {
        const abortCont = new AbortController()
        if (selectedVersion > 0) loadAnnotations(abortCont.signal)
        return () => abortCont.abort("Stopped annotation stream")
    }, [selectedVersion]) // eslint-disable-line

    useEffect(() => {
        if (!sysGsAnnotations.length && !modifiedGsAnnotations.length) return

        const worker = new Worker(new URL('../../workers/marker.worker.ts', import.meta.url))

        worker.onmessage = (e) => {
            setLayerData(e.data)
            setSysMarkerFilter(e.data.category)
            worker.terminate()
        }

        worker.postMessage({
            sysGsAnnotations,
            modifiedGsAnnotations,
            colorDict
        })

        return () => worker.terminate()
    }, [sysGsAnnotations, modifiedGsAnnotations, colorDict])

    const getTooltip = useCallback((info: PickingInfo<Annotation>) => {
        if (info.object) {
            return {
                html: `
                    ${showMapCoords && info.coordinate ? `<div>X: ${info.coordinate[0] | 0}</div>` : ''}
                    ${showMapCoords && info.coordinate ? `<div>Y: ${info.coordinate[1] | 0}</div>` : ''}
                    ${info.object.name ? `<div>${info.object.name}</div>` : ''}
                    ${info.object.area ? `<div>Area: ${info.object.area} mm²</div>` : ''}
                    ${info.object.distance ? `<div>${info.object.distance} mm</div>` : ''}
                `
            }
        } else if (
            showMapCoords && info.coordinate &&
            info.coordinate[0] >= 0 && info.coordinate[0] <= mapProp!.width &&
            info.coordinate[1] >= 0 && info.coordinate[1] <= mapProp!.height
        ) {
            return {
                html: `
                    <div>X: ${info.coordinate[0] | 0}</div>
                    <div>Y: ${info.coordinate[1] | 0}</div>
                `
            }
        }
    }, [showMapCoords])

    const flyToPoint = useCallback((x: number, y: number) => {
        setInitialViewState((prevState: any) => ({
            ...prevState,
            target: [x, y, 0],
            zoom: 4,
            transitionDuration: 800,
            transitionInterpolator: new LinearInterpolator({
                transitionProps: ['target', 'zoom']
            })
        }))
    }, [])

    const handlePointLoc = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPointLoc(prevState => ({...prevState, [event.target.name]: Number(event.target.value)}))
    }

    const togglePointLoc = () => {
        setPointLoc({x: 0, y: 0})
        setLocatePoint(prevState => !prevState)
    }

    const handleVersionChange = async (data: Version) => {
        setSelectedVersion(data.id)
    }

    const MemoizedVersionsList = useMemo(() =>
        <Versions
            imageData={imageData}
            activeVerId={activeVerId}
            sysMarkerVer={sysMarkerVer}
            versions={versions}
            isMarkerLoading={isDataLoading}
            handleVersionChange={handleVersionChange}
        />
    , [activeVerId, versions, imageData, sysMarkerVer, isDataLoading])

    const handleToggle = (layerName: string) => {
        setHighlightMarkerId('') // Clear marker highlight
        setActiveMarkerLayer(prev => (prev === layerName ? '' : layerName))
    }

    useEffect(() => {
        console.log(activeMarkerLayer)
    }, [activeMarkerLayer])

    const showByCategory = (category: string) => {
        setSysMarkerFilter(prevList => {
            if (prevList.includes(category)) return prevList.filter(i => i!==category)
            else return [...prevList, category]
        })
        handleToggle(`system-${category}`)
    }
    const zoomToPolygon = (markerId: string) => {
        setHighlightMarkerId(markerId)
        try {
            const index = layerData.polygons.findIndex(i => i.id === markerId)
            if (index !== -1) {
                const marker = layerData.polygons[index]
                flyToPoint(marker.coordinates[0][0], marker.coordinates[0][1])
            }
        } catch (err) {
            console.log(err)
        }
    }

    const MemoizedSysAnnotationsList = useMemo(() =>
        <SysAnnotationsList
            sysMarkers={sysGsAnnotations}
            userMarkers={modifiedGsAnnotations}
            showByCategory={showByCategory}
            zoomToMarker={zoomToPolygon}
            sysAnnoLoader={sysMarkerLoader}
            colorLib={colorLib}
            filter={sysMarkerFilter}
        />
    , [sysGsAnnotations, modifiedGsAnnotations, sysMarkerLoader, colorLib, sysMarkerFilter])  // eslint-disable-line

    const showByUser = (userId: number) => {
        setUserMarkerFilter(prevList => {
            if (prevList.includes(userId)) return prevList.filter(i => i!==userId)
            else return [...prevList, userId]
        })
    }

    const MemoizedUserAnnotationsList = useMemo(() =>
        <UserAnnotationsList
            userMarkers={modifiedGsAnnotations}
            showByUser={showByUser}
            zoomToMarker={zoomToPolygon}
            userAnnoLoader={userMarkerLoader}
            filter={userMarkerFilter}
        />
    , [modifiedGsAnnotations, userMarkerLoader, userMarkerFilter])  // eslint-disable-line

    return (
        <ViewerContext.Provider value={{imageData, mapProp, colorLib}}>

        <Box className="position-relative d-flex w-100 gap-1 map-height">
            {mapProp
                ? <>
                    <DeckGL
                        views={views}
                        initialViewState={initialViewState}
                        controller={true}
                        layers={layers}
                    >
                        <InfoWidget
                            mode="hover"
                            getTooltip={getTooltip}
                            style={{fontSize: '1rem', fontWeight: '500'}}
                        />
                    </DeckGL>

                    {/* Slide name */}
                    <Box sx={{
                        position: 'absolute',
                        top: '1rem',
                        left: '1rem',
                        width: 'fit-content',
                        height: 'fit-content',
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        backdropFilter: "blur(8px)",
                        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                        borderRadius: "12px",
                        px: 2,
                        pt: 0.5,
                        pb: 0.25
                    }}>
                        <Typography
                            component="h1"
                            variant="caption"
                            sx={{
                                display: "block",
                                maxWidth: "40vw",
                                color: "text.primary",
                                fontWeight: 400,
                                letterSpacing: "0.5px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                            }}
                        >
                            {imageData.name.split("/").pop()}
                        </Typography>
                    </Box>

                    {/* Versions list */}
                    <Box sx={{
                        position: 'absolute',
                        bottom: '1rem',
                        left: '1rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-end',
                        gap: 5
                    }}>
                        {MemoizedVersionsList}
                    </Box>

                    {/* Coordinates locate */}
                    <Box sx={{
                        position: 'absolute',
                        bottom: '1rem',
                        right: '1rem',
                        display: 'flex',
                        justifyContent: 'end',
                        alignItems: 'flex-end',
                        gap: 5
                    }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            justifyContent: 'flex-end',
                            gap: 2,
                            p: 1,
                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                            backdropFilter: "blur(8px)",
                            border: '2px solid #ccc',
                            borderRadius: 2,
                        }}>
                            {locatePoint && (
                                <Box
                                    component="form"
                                    noValidate
                                    autoComplete="off"
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                        width: 100,
                                    }}
                                >
                                    {['x', 'y'].map(axis => (
                                        <TextField
                                            key={axis}
                                            name={axis}
                                            placeholder={axis.toUpperCase()}
                                            hiddenLabel
                                            variant="filled"
                                            size="small"
                                            sx={{ bgcolor: "white" }}
                                            onChange={handlePointLoc}
                                        />
                                    ))}
                                    <Button onClick={() => flyToPoint(pointLoc.x, pointLoc.y)} variant="contained">View</Button>
                                </Box>
                            )}
                            <Box sx={{
                                display: 'flex',
                                gap: 1.5,
                                alignItems: 'center',
                                color: 'var(--toolgrey)'
                            }}>
                                <IconButton
                                    sx={iconButtonSx}
                                    onClick={() => setShowMapCoords(prevState => !prevState)}
                                    aria-label={showMapCoords ? "Hide Coordinates" : "Show Coordinates"}
                                >
                                    {showMapCoords ? <GrFormView /> : <GrFormViewHide />}
                                </IconButton>
                                <IconButton sx={iconButtonSx} onClick={togglePointLoc}>
                                    {locatePoint ? <MdOutlineMyLocation /> : <MdLocationSearching />}
                                </IconButton>
                            </Box>
                        </Box>
                    </Box>
                    
                    {/* Right side bar */}
                    <IconButton
                        onClick={() => setDrawerOpen(true)}
                        aria-label="Open sidebar"
                        sx={{
                            position: "absolute",
                            top: "1rem",
                            right: "1rem",
                            zIndex: 999,
                            borderRadius: 2,
                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                            backdropFilter: "blur(8px)",
                            border: '2px solid #ccc'
                        }}
                    >
                        <FaChevronLeft size={20} />
                    </IconButton>
                    <Drawer
                        open={drawerOpen}
                        anchor="right"
                        variant="persistent"
                        PaperProps={{sx: {position: 'absolute', zIndex: 999}, elevation: 3}}
                    >
                        <Box className="d-flex align-items-center p-1 justify-content-start">
                            <IconButton onClick={() => setDrawerOpen(false)}>
                                <ChevronRight />
                            </IconButton>
                        </Box>

                        <div className='p-1'>
                            <Accordion as="h6">
                                <Accordion.Item key='1' eventKey="1">
                                    <span className="d-flex align-items-center justify-content-center">
                                        <input
                                            type="checkbox"
                                            className="form-check-input m-0 ms-3 me-2"
                                            style={{cursor: "pointer"}}
                                            checked={activeMarkerLayer === 'system'}
                                            onChange={() => handleToggle('system')}
                                        />
                                        <Accordion.Header className="flex-grow-1 no-shadow btn-gap">
                                            <Typography>System Annotations</Typography>
                                        </Accordion.Header>
                                    </span>
                                    <Accordion.Body>
                                        {MemoizedSysAnnotationsList}
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>

                            <Accordion as="h6">
                                <Accordion.Item key='2' eventKey="2">
                                    <span className="d-flex align-items-center justify-content-center">
                                        <input
                                            type="checkbox"
                                            className="form-check-input m-0 ms-3 me-2"
                                            style={{cursor: "pointer"}}
                                            checked={activeMarkerLayer === 'user'}
                                            onChange={() => handleToggle('user')}
                                        />
                                        <Accordion.Header className="flex-grow-1 no-shadow btn-gap">
                                            <Typography>User Annotations</Typography>
                                        </Accordion.Header>
                                    </span>
                                    <Accordion.Body>
                                        {MemoizedUserAnnotationsList}
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>

                            <Accordion as="h6">
                                <Accordion.Item key='3' eventKey="3">
                                    <Accordion.Header className="no-shadow btn-gap">
                                        <Typography>Diagnostic Predictions</Typography>
                                    </Accordion.Header>
                                    <Accordion.Body style={{ position: 'relative' }}>
                                        <Box sx={{position: 'absolute', right: '1rem', top: '1rem', zIndex: 2}}>
                                            <IconButton onClick={() => {}} disabled={true} title="Reload">
                                                <Refresh />
                                            </IconButton>
                                        </Box>
                                        {/* {predResultLoading
                                            ? <Stack spacing={2} className='p-2'>
                                                {Array.from({length: 10}, (_, i) => i).map(i => (<Skeleton key={i} variant="rounded" height={24} />))}
                                            </Stack>
                                            : PredResultList
                                        } */}
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>

                            <Accordion as="h6">
                                <Accordion.Item key='4' eventKey='4'>
                                    <span onClick={() => {}} className="d-flex align-items-center justify-content-center">
                                        <input
                                            id="check-user-bcr"
                                            type="checkbox"
                                            className="form-check-input m-0 ms-3 me-2 toggle_slide_anno"
                                            style={{cursor: "pointer"}}
                                            onChange={() => {}}
                                        />
                                        <Accordion.Header className="flex-grow-1 no-shadow btn-gap">
                                            <Typography>Prognostic Outcome Predictions</Typography>
                                        </Accordion.Header>
                                    </span>
                                    <Accordion.Body className='position-relative'>
                                        <IconButton className='bcr-score-reload' onClick={() => {}} size='small'>
                                            <Refresh />
                                        </IconButton>
                                        {BCRScore!==null
                                            ? BCRScore
                                                ? <>
                                                    <Typography>PathomIQ Score: {BCRScore}</Typography>
                                                    {BCRScore > 0.55
                                                        ? <Typography sx={{width: 'fit-content'}} className='text-danger'>Patient is at high risk of metastasis</Typography>
                                                        : BCRScore < 0.45
                                                            ? <Typography sx={{width: 'fit-content'}} className='text-primary'>Patient is at low risk of biochemical recurrence and metastasis</Typography>
                                                            : <Typography sx={{width: 'fit-content'}} className='text-primary'>Patient is at high risk of biochemical recurrence</Typography>
                                                    }
                                                </>
                                                : <Typography className='text-secondary'>No score</Typography>
                                            : <Skeleton variant="rounded" height={48} />
                                        }
                                        <br/>
                                        {/* {MemoizedRankedTiles} */}
                                        <br/>
                                        {/* {MemoizedBCRHeatTypes} */}
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>

                            {isPrivate
                                ? <Accordion as="h6">
                                    <Accordion.Item key='5' eventKey='5'>
                                        <Accordion.Header onClick={() => {}} className="no-shadow btn-gap">
                                            <Typography>Treatment Outcome Predictions</Typography>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <Typography className='text-secondary'>Coming soon</Typography>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>
                                : null
                            }

                            {isPrivate
                                ? <Accordion as="h6">
                                    <Accordion.Item key='6' eventKey='6'>
                                        <Accordion.Header onClick={() => {}} className="no-shadow btn-gap">
                                            <Typography>Cancer Genotype Predictions</Typography>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <Typography className='text-secondary'>Coming soon</Typography>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>
                                : null
                            }

                            {isPrivate
                                ? <Accordion as="h6">
                                    <Accordion.Item key='7' eventKey='7'>
                                        <Accordion.Header className="no-shadow btn-gap">
                                            <Typography>Genomic Predictions</Typography>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <Accordion>
                                                {/* {genomicPredList.length
                                                    ? genomicPredList.map((i: any) => {
                                                        if (!i.length) return <Typography key={i} className='text-secondary'>N/A</Typography>
                                                        const name = i.split('/').filter((x: string) => x.length).slice(-1)[0]
                                                        return (
                                                            <Accordion.Item key={i} eventKey={i}>
                                                                <Accordion.Header className="no-shadow" onClick={() => showGenome(i, imageData.name, imageData.output_s3, name)}>
                                                                    <Typography className='text-capitalize'>{name}</Typography>
                                                                </Accordion.Header>
                                                                <Accordion.Body>
                                                                    {geneTypeList.list.length
                                                                        ? <>
                                                                            <FormControl className='w-100 mb-3' variant="outlined">
                                                                                <InputLabel htmlFor="outlined-adornment-search">Search</InputLabel>
                                                                                <OutlinedInput
                                                                                    id="outlined-adornment-search"
                                                                                    type='text'
                                                                                    onChange={handleGeneNameSearch}
                                                                                    value={geneNameSearchKey}
                                                                                    endAdornment={
                                                                                        <InputAdornment position="end">
                                                                                            <IconButton
                                                                                                aria-label='search gene name'
                                                                                                onClick={handleCloseGeneNameSearch}
                                                                                                edge="end"
                                                                                                disabled={!geneNameSearchKey.length}
                                                                                            >
                                                                                                <Close />
                                                                                            </IconButton>
                                                                                        </InputAdornment>
                                                                                    }
                                                                                    label="Search"
                                                                                />
                                                                            </FormControl>
                                                                            <LazyList items={geneTypeList.list} searchKey={geneNameSearchKey} /> 
                                                                        </>
                                                                        : <CircularProgress size={24} />
                                                                    }
                                                                </Accordion.Body>
                                                            </Accordion.Item>
                                                        )
                                                    })
                                                    : <LoadingBtn />
                                                } */}
                                            </Accordion>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>
                                : null
                            }

                            <Accordion as="h6">
                                <Accordion.Item key='8' eventKey='8'>
                                    <Accordion.Header className="no-shadow btn-gap">
                                        <Typography>Comments</Typography>
                                    </Accordion.Header>
                                    <Accordion.Body style={{maxWidth: 500}}>
                                        {/* {MemoizedChat} */}
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>

                            <Accordion as="h6">
                                <Accordion.Item key='9' eventKey='9'>
                                    <Accordion.Header className="no-shadow btn-gap">
                                        <Typography>XML Files</Typography>
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        {/* {MemoizedXmlList} */}
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>

                            {isPrivate
                                ? <Accordion as="h6">
                                    <Accordion.Item key='10' eventKey="10">
                                        <Accordion.Header className="no-shadow btn-gap">
                                            <Typography>JSON Files</Typography>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            {/* {MemoizedJsonList} */}
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>
                                : null
                            }

                            <Accordion>
                                <Box className="p-3">
                                    <span className="d-flex align-items-center">
                                        <input
                                            id="tissue-outline"
                                            type="checkbox"
                                            className="form-check-input m-0 me-2 toggle_slide_anno"
                                            style={{ cursor: "pointer" }}
                                            onChange={() => {}}
                                        />
                                        <Typography>Show tissue outline</Typography>
                                    </span>
                                </Box>
                            </Accordion>

                            {isPrivate && <Accordion>
                                <Box className="p-3">
                                    <Button
                                        size="small"
                                        href={`/viewer-v2/${imageData.id}`}
                                        variant="contained"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <OpenInNew color="inherit" fontSize='small' />&nbsp;&nbsp;
                                        New viewer preview
                                    </Button>
                                </Box>
                            </Accordion>}
                        </div>
                    </Drawer>
                </>
                : isMapLoading
                    ? <LoadingAnimeSpinner />
                    : mapError ? <MapWarningTexts text={"Slide viewer failed to initialize"} /> : null
            }

            {/* bottom line loading animation */}
            {isDataLoading &&
                <Box className='position-absolute' sx={{ bottom: 0, left: 'calc(50vw - 35px)' }}>
                    <Dotpulse width={70} height={63} />
                </Box>
            }
        </Box>
        
        </ViewerContext.Provider>
    )
}

export default ImageViewer
